import {
  type RosterItemResponse,
  type UpdatePersonSportOrganizationRequest,
  v1PeopleDelete,
  v1PersonSportOrganizationsDetail,
  v1PersonSportOrganizationsUpdate,
} from '@on3/api';
import { Form } from '@on3/ui-lib/components/Form';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { useClickOutside } from '@on3/ui-lib/hooks/useClickOutside';
import { externalApi } from '@on3/ui-lib/index';
import { Check, XMark } from '@on3/ui-lib/src/components/Svg';
import { filterNumber } from '@on3/ui-lib/utils/filters';
import { yup } from '@on3/ui-lib/utils/yup';
import { RosterWrapperContext } from 'hocs/Roster/RosterWrapper';
import { useRouter } from 'next/navigation';
import { useContext, useEffect, useReducer, useRef } from 'react';
import { createTitle } from 'utils/meta';

import styles from './Edit.module.scss';

interface RosterState {
  fetching: boolean;
  processing: boolean;
  values: UpdatePersonSportOrganizationRequest | null;
  errors: Record<string, string> | null;
  saved: boolean;
  message: string | null;
}

type ActionType =
  | { type: 'fetch' }
  | { type: 'init'; payload: UpdatePersonSportOrganizationRequest }
  | { type: 'update'; field: string; value: any }
  | { type: 'submit' }
  | { type: 'success'; payload: string | null }
  | { type: 'error'; payload: Record<string, string> | null };

const schema = yup.object().shape({
  jerseyNumber: yup.string().nullable(),
});

const reducer = (state: RosterState, action: ActionType): RosterState => {
  switch (action.type) {
    case 'fetch':
      return { ...state, fetching: true };
    case 'init':
      return {
        ...state,
        fetching: false,
        values: action.payload,
      };
    case 'update':
      return {
        ...state,
        values: {
          ...(state.values as UpdatePersonSportOrganizationRequest),
          [action.field]: action.value,
        },
        saved: false,
      };
    case 'submit':
      return { ...state, processing: true };
    case 'success':
      return {
        ...state,
        processing: false,
        saved: true,
        message: action.payload,
      };
    case 'error':
      return { ...state, errors: action.payload };
    default:
      return state;
  }
};

export const Edit = ({ rosterItem }: { rosterItem: RosterItemResponse }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const { close } = useContext(RosterWrapperContext);
  const { player, psoKey } = rosterItem || {};

  const playerName = createTitle(player?.firstName, player?.lastName);

  useClickOutside(ref, () => close());

  const [state, dispatch] = useReducer(reducer, {
    fetching: true,
    processing: false,
    values: {
      jerseyNumber: player?.jerseyNumber ?? undefined,
    } as UpdatePersonSportOrganizationRequest,
    errors: null,
    saved: false,
    message: null,
  });

  useEffect(() => {
    v1PersonSportOrganizationsDetail(externalApi, psoKey)
      .then((data) => {
        dispatch({ type: 'init', payload: data });
      })
      .catch((error) => {
        dispatch({ type: 'error', payload: error });
      });
  }, [psoKey]);

  const handleJerseyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    dispatch({
      type: 'update',
      field: 'jerseyNumber',
      value: filterNumber(raw) ? Number(raw) : undefined,
    });
  };

  const onSubmit = async (data: UpdatePersonSportOrganizationRequest) => {
    try {
      dispatch({ type: 'error', payload: null });
      dispatch({ type: 'submit' });
      await v1PersonSportOrganizationsUpdate(externalApi, psoKey, data);
      dispatch({
        type: 'success',
        payload:
          'Roster item updated successfully. It may take time to see updates due to caching.',
      });
    } catch (e) {
      dispatch({
        type: 'error',
        payload: { message: 'Error updating person sport organization.' },
      });
    }
  };

  const handleDelete = async () => {
    if (
      !player?.key ||
      !window.confirm(
        `Are you sure you want to delete ${playerName} (${player.key})? Please double check this is what you want to do.`,
      ) ||
      !window.confirm(`Are you positive you want to delete ${playerName}?`)
    )
      return;

    try {
      dispatch({ type: 'submit' });
      dispatch({ type: 'error', payload: null });
      await v1PeopleDelete(externalApi, player.key);
      dispatch({
        type: 'success',
        payload: 'Roster item deleted successfully. Refreshing page data.',
      });
      router.refresh();
    } catch (e) {
      dispatch({
        type: 'error',
        payload: { message: 'Error deleting roster item.' },
      });
    }
  };

  return (
    <div className={styles.popout} ref={ref}>
      <button name="close" onClick={close} type="button">
        <XMark />
      </button>

      <Form
        className={styles.form}
        onSubmit={onSubmit}
        schema={schema}
        values={state.values}
      >
        {({ processing, errors }) => (
          <>
            <header>
              <h1 className={styles.title}>Edit Roster Item</h1>
              <p>
                Editing data for{' '}
                <strong>
                  {playerName} ({player?.key})
                </strong>
              </p>
            </header>

            <Input
              error={errors?.jerseyNumber}
              inputMode="numeric"
              label="Jersey number"
              onChange={handleJerseyChange}
              type="text"
              value={state.values?.jerseyNumber ?? ''}
            />

            <button
              data-saved={state.saved}
              disabled={processing}
              type="submit"
            >
              {state.saved ? (
                <>
                  <span>Saved</span>
                  <Check />
                </>
              ) : (
                'Save'
              )}
            </button>

            {state.message && <p className={styles.message}>{state.message}</p>}

            <footer className={styles.footer}>
              <button
                disabled={state.processing}
                onClick={handleDelete}
                type="button"
              >
                Delete Roster Item
              </button>
            </footer>
          </>
        )}
      </Form>
    </div>
  );
};
